import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { MianAvatar } from '@hero/web-components';
import { useCurrentUser } from '@hero/hooks-shared';
import { useFeature } from '@hero/web-context';
import { route } from '@hero/web-hooks';
import { VehicleMainIcon } from '@hero/web-icons';
import { getApplicationsCardsData } from '@/data/staticData';

/**
 * Non-generic alias around `route()` for call sites that resolve a
 * `RouteName` union at runtime (the More cards' `item.routeName`)
 * and for fixed-name calls inside JSX. The generic variadic
 * signature on `route<T>` triggers a known TypeScript 5.4 compiler
 * issue when called inside JSX without a second argument
 * (`Expected 2 arguments, but got 1` even when the route's params
 * are fully optional) — routing those call sites through a
 * `string`-typed wrapper sidesteps the bad inference path.
 */
const routeByName = route as unknown as (name: string) => string;

/**
 * Card layout — ratios chosen so the page feels designed, not assembled:
 *
 *  - Icon glyph: 32px (`size-8`) inside a 64px tinted square
 *    (`size-16 rounded-2xl bg-primary-50`). 32/64 = 50% glyph-to-tile —
 *    the same ratio Material/iOS use for app-icon launchers, so the
 *    visual weight reads as "icon" rather than "isolated svg".
 *  - Card height: `h-44` (~176px). 64px icon + ~24px title + ~32px
 *    two-line description + 24px top/bottom padding lands inside the
 *    card without dead space.
 *  - Description column: `max-w-[14rem]` so a one-line and a two-line
 *    description occupy the same horizontal band — the title baseline
 *    no longer jumps when one card wraps and another doesn't.
 *  - Spacing: `gap-2` between icon-tile / title / description gives a
 *    consistent rhythm and removes the special-case `pt-3` / `mb-6`
 *    spacing the original card had.
 */
const cardClasses =
  'group flex flex-col items-center justify-center gap-2 bg-white border rounded-lg h-44 p-4 text-center transition-colors';
const iconTileClasses =
  'size-16 rounded-2xl bg-primary-50 group-hover:bg-primary-100 flex items-center justify-center transition-colors';
const titleClasses = 'text-sm font-semibold text-gray-900';
const descriptionClasses = 'text-xs text-gray-500 leading-snug max-w-[14rem]';

export default function ApplicationsCards() {
  const { t } = useTranslation(['a-carrier-dashboard']);
  const { user, isDriver } = useCurrentUser();

  const userId = user?._id;
  const fleetModule = useFeature('fleet_module');
  const financingModule = useFeature('financing_module');
  /** Fleet lives in the sidebar when financing is off; keep it on More only when financing holds that slot. */
  const showFleetCard =
    user != null && !isDriver && fleetModule.isEnabled && financingModule.isEnabled;
  /** Apps & Integrations marketplace gate (reactive so the card hides when the flag flips). */
  const appsModule = useFeature('apps_module');
  /** Billing & subscription gate — hides the More card when the parent flag is off. */
  const billingModule = useFeature('billing_module');

  const flagState: Partial<Record<string, boolean>> = {
    apps_module: appsModule.isEnabled,
    billing_module: billingModule.isEnabled,
  };

  const cardsData = (user ? getApplicationsCardsData(user) : []).filter(
    item => !item.featureFlag || (flagState[item.featureFlag] ?? true),
  );

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
      <Link
        to={userId ? routeByName('settings_profile') : '#'}
        className={`${cardClasses} hover:border-primary-200`}>
        <MianAvatar src="/images/avatar.png" className="size-16" />
        <h3 className={titleClasses}>{t('a-carrier-dashboard:more_page.profile.title')}</h3>
        <p className={descriptionClasses}>
          {t('a-carrier-dashboard:more_page.profile.description')}
        </p>
      </Link>

      {showFleetCard && (
        <Link
          to={routeByName('fleet')}
          className={`${cardClasses} hover:border-primary-200`}>
          <div className={iconTileClasses}>
            <VehicleMainIcon className="size-8 text-primary" strokeWidth={1.5} />
          </div>
          <h3 className={titleClasses}>{t('a-carrier-dashboard:more_page.fleet.title')}</h3>
          <p className={descriptionClasses}>
            {t('a-carrier-dashboard:more_page.fleet.description')}
          </p>
        </Link>
      )}

      {cardsData.map(item => {
        const Visual = item.Icon ? (
          <div className={iconTileClasses}>
            <item.Icon className="size-8 text-primary" strokeWidth={1.5} />
          </div>
        ) : item.img ? (
          <img src={item.img} alt={item.title} className="size-16" />
        ) : null;

        const linkTo = routeByName(item.routeName);
        return (
          <Link
            key={item.id}
            to={linkTo}
            className={`${cardClasses} relative hover:border-primary-200`}>
            {item.badge === 'soon' && (
              <span className="absolute end-3 top-3 rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                {t('a-carrier-dashboard:more_page.apps.soon_badge')}
              </span>
            )}
            {Visual}
            <h3 className={titleClasses}>{t(item.title as never)}</h3>
            <p className={descriptionClasses}>{t(item.description as never)}</p>
          </Link>
        );
      })}
    </div>
  );
}
